import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useSchool } from "@/context/SchoolContext";
import { initials } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { IdCard, Loader2, Printer, Settings2, Eye, Search, LayoutTemplate, Palette, Save, PanelsTopLeft, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import { UseTemplateDialog } from "@/components/UseTemplateDialog";
import "./report-card.css";

const FIELD_TOGGLES = [
  ["logo", "School Logo"], ["school_name", "School Name"], ["tag_line", "Tag Line"],
  ["school_code", "School Code"], ["affiliated_to", "Affiliated To"], ["udise_code", "UDISE Code"],
  ["address", "Address"], ["school_number", "Phone"], ["watermark_logo", "Watermark"],
];
const ALL_KEYS = FIELD_TOGGLES.map((k) => k[0]);
const fmtDate = (d) => {
  if (!d) return "—";
  const p = String(d).split("-");
  return p.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : d;
};

const SAMPLE_STUDENT = { id: "sample", name: "Krishna Yadav", class_name: "3rd", section: "A", roll_number: "5", admission_number: "1", father_name: "Ranjeet Singh" };
const SAMPLE_EXAM = { name: "Half Yearly Examination", term: "Term 1", class_name: "3rd", subjects: [
  { subject_id: "1", subject_name: "English", date: "2026-07-15", day: "Wednesday", start_time: "09:00", end_time: "11:00" },
  { subject_id: "2", subject_name: "Mathematics", date: "2026-07-17", day: "Friday", start_time: "09:00", end_time: "11:30" },
  { subject_id: "3", subject_name: "Science", date: "2026-07-19", day: "Sunday", start_time: "09:00", end_time: "11:00" },
] };

function AdmitCardView({ student: st, exam, school }) {
  const acs = school?.admit_card_settings || {};
  const fields = acs.fields;
  const show = (k) => !fields || fields.length === 0 || fields.includes(k);
  return (
    <div className="report-card bg-white p-8 text-slate-800 relative overflow-hidden">
      {show("watermark_logo") && school?.watermark_logo && (
        <img src={school.watermark_logo} alt="" aria-hidden className="pointer-events-none select-none absolute inset-0 m-auto max-w-[55%] max-h-[55%] opacity-[0.06] object-contain" />
      )}
      <div className="relative text-center border-b-2 border-slate-800 pb-3 mb-4 flex items-center justify-center gap-3">
        {show("logo") && school?.logo && <img src={school.logo} alt="logo" className="w-14 h-14 object-contain shrink-0" />}
        <div className="min-w-0">
          {show("school_name") && <h2 className="text-2xl font-extrabold tracking-tight">{school?.name || "SSSPS"}</h2>}
          {show("tag_line") && school?.tag_line && <p className="text-xs text-slate-500 italic">{school.tag_line}</p>}
          <div className="text-[11px] text-slate-500 mt-0.5 flex flex-wrap justify-center gap-x-3">
            {show("school_code") && school?.school_code && <span>Code: {school.school_code}</span>}
            {show("affiliated_to") && school?.affiliated_to && <span>Affiliated to: {school.affiliated_to}</span>}
            {show("udise_code") && school?.udise_code && <span>UDISE: {school.udise_code}</span>}
          </div>
          {show("address") && school?.address && <p className="text-[11px] text-slate-500">{school.address}</p>}
          {show("school_number") && school?.school_number && <p className="text-[11px] text-slate-500">Ph: {school.school_number}</p>}
        </div>
      </div>

      <div className="relative text-center mb-4">
        <span className="inline-block px-4 py-1 rounded-md bg-slate-900 text-white text-sm font-bold tracking-widest">{acs.title || "ADMIT CARD"}</span>
        <p className="text-sm text-slate-500 mt-2">{exam.name}{exam.term ? ` — ${exam.term}` : ""}</p>
      </div>

      <div className="relative grid grid-cols-2 gap-x-8 gap-y-1 text-sm mb-4">
        <div><b>Name:</b> {st.name}</div>
        <div><b>Class:</b> {st.class_name} {st.section}</div>
        <div><b>Roll No:</b> {st.roll_number || "—"}</div>
        <div><b>Adm No:</b> {st.admission_number}</div>
        <div><b>Father:</b> {st.father_name || "—"}</div>
      </div>

      <table className="w-full text-sm border border-slate-300 border-collapse mb-4">
        <thead className="bg-slate-100">
          <tr>{["Subject", "Date", "Day", "Timing"].map((h) => <th key={h} className="border border-slate-300 px-2 py-1.5 text-left">{h}</th>)}</tr>
        </thead>
        <tbody>
          {(exam.subjects || []).map((s) => (
            <tr key={s.subject_id}>
              <td className="border border-slate-300 px-2 py-1.5 font-medium">{s.subject_name}</td>
              <td className="border border-slate-300 px-2 py-1.5">{fmtDate(s.date)}</td>
              <td className="border border-slate-300 px-2 py-1.5">{s.day || "—"}</td>
              <td className="border border-slate-300 px-2 py-1.5">{s.start_time && s.end_time ? `${s.start_time} - ${s.end_time}` : "—"}</td>
            </tr>
          ))}
          {(exam.subjects || []).length === 0 && <tr><td colSpan={4} className="border border-slate-300 px-2 py-3 text-center text-slate-400">No subjects scheduled.</td></tr>}
        </tbody>
      </table>

      <div className="flex justify-between mt-12 text-sm text-slate-500">
        <div className="text-center"><div className="border-t border-slate-400 pt-1 px-6">{acs.sig_left || "Controller of Exams"}</div></div>
        <div className="text-center"><div className="border-t border-slate-400 pt-1 px-6">{acs.sig_right || "Principal"}</div></div>
      </div>
    </div>
  );
}

export default function AdmitCards() {
  const nav = useNavigate();
  const { school, refreshSchool } = useSchool();
  const [exams, setExams] = useState([]);
  const [singleClass, setSingleClass] = useState("");
  const [examId, setExamId] = useState("");
  const [examData, setExamData] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [card, setCard] = useState(null);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [filterIds, setFilterIds] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [tplOpen, setTplOpen] = useState(false);
  const [bindOpen, setBindOpen] = useState(false);

  const boundTpl = school?.admit_card_settings?.template_id
    ? { id: school.admit_card_settings.template_id, name: school.admit_card_settings.template_name || "Canvas layout" }
    : null;

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [savingCs, setSavingCs] = useState(false);
  const [fields, setFields] = useState(ALL_KEYS);
  const [sigLeft, setSigLeft] = useState("Controller of Exams");
  const [sigRight, setSigRight] = useState("Principal");
  const [title, setTitle] = useState("ADMIT CARD");

  useEffect(() => { api.get("/exams").then((r) => setExams(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (!examId) { setExamData(null); setStudents([]); return; }
    setLoading(true);
    setSelected(new Set());
    api.get(`/exams/${examId}`).then(async (r) => {
      setExamData(r.data);
      const sr = await api.get("/students", { params: { class_id: r.data.class_id, limit: 1000 } });
      setStudents(sr.data.students || []);
    }).catch((e) => toast.error(apiErr(e.response?.data?.detail))).finally(() => setLoading(false));
  }, [examId]);

  const classesFromExams = Array.from(new Map(exams.map((e) => [e.class_id, e.class_name])).entries());

  const filtered = students.filter((s) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [s.name, s.admission_number, s.roll_number, s.class_name].filter(Boolean).join(" ").toLowerCase().includes(q);
  });

  const toggleOne = (id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allChecked = filtered.length > 0 && filtered.every((s) => selected.has(s.id));
  const toggleAll = () => setSelected(() => allChecked ? new Set() : new Set(filtered.map((s) => s.id)));

  const pdfFromTemplate = async (ids) => {
    try {
      const r = await api.post(`/school/templates/${boundTpl.id}/pdf`, { student_ids: ids, exam_id: examId, origin: window.location.origin }, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch (e) { toast.error("Could not generate from the bound layout"); }
  };
  const openCard = (sid) => { const st = students.find((s) => s.id === sid); if (st && examData) setCard({ student: st, exam: examData, school }); };
  const preview = (sid) => openCard(sid);
  const generate = (sid) => {
    if (boundTpl && examId) { setBusyId(sid); pdfFromTemplate([sid]).finally(() => setBusyId(null)); return; }
    setBusyId(sid); openCard(sid); setTimeout(() => { document.body.classList.remove("printing-bulk"); window.print(); setBusyId(null); }, 400);
  };
  const printSingle = () => { document.body.classList.remove("printing-bulk"); window.print(); };
  const printList = (ids) => {
    if (boundTpl && examId) { pdfFromTemplate(ids || students.map((s) => s.id)); return; }
    if (!students.length) return;
    setFilterIds(ids);
    document.body.classList.add("printing-bulk");
    setTimeout(() => window.print(), 180);
    const cleanup = () => { document.body.classList.remove("printing-bulk"); setFilterIds(null); window.removeEventListener("afterprint", cleanup); };
    window.addEventListener("afterprint", cleanup);
    setTimeout(cleanup, 3000);
  };
  const bindTemplate = async (tpl) => {
    try {
      const acs = { ...(school?.admit_card_settings || {}), template_id: tpl.id, template_name: tpl.name };
      await api.put("/settings", { ...school, admit_card_settings: acs });
      await refreshSchool();
      toast.success(`Bound "${tpl.name}" as the printed layout`);
    } catch (e) { toast.error("Could not bind layout"); }
  };
  const unbindTemplate = async () => {
    try {
      const acs = { ...(school?.admit_card_settings || {}) }; delete acs.template_id; delete acs.template_name;
      await api.put("/settings", { ...school, admit_card_settings: acs });
      await refreshSchool();
      toast.success("Reverted to the default admit card");
    } catch (e) { toast.error("Could not update"); }
  };

  const openSettings = () => {
    const acs = school?.admit_card_settings || {};
    setFields(acs.fields && acs.fields.length ? [...acs.fields] : [...ALL_KEYS]);
    setSigLeft(acs.sig_left || "Controller of Exams");
    setSigRight(acs.sig_right || "Principal");
    setTitle(acs.title || "ADMIT CARD");
    setSettingsOpen(true);
  };
  const toggleField = (k) => setFields((p) => p.includes(k) ? p.filter((x) => x !== k) : [...p, k]);
  const saveSettings = async () => {
    setSavingCs(true);
    try {
      const acs = { ...(school?.admit_card_settings || {}), fields, sig_left: sigLeft, sig_right: sigRight, title };
      await api.put("/settings", { ...school, admit_card_settings: acs });
      await refreshSchool();
      toast.success("Admit card settings saved");
      setSettingsOpen(false);
    } catch (e) { toast.error("Could not save settings"); }
    finally { setSavingCs(false); }
  };

  const previewSchool = { ...(school || {}), admit_card_settings: { ...(school?.admit_card_settings || {}), fields, sig_left: sigLeft, sig_right: sigRight, title } };
  const previewStudent = students[0] || SAMPLE_STUDENT;
  const previewExam = examData || SAMPLE_EXAM;

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900 flex items-center gap-2"><IdCard className="text-indigo-500" size={26} />Admit Card</h1>
          <p className="text-slate-500">Generate printable admit cards with the exam schedule for each student.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" className="gap-1.5" onClick={() => { if (!filtered.length) { toast.error("Select an exam with students first"); return; } setTplOpen(true); }} data-testid="ac-use-template-btn"><LayoutTemplate size={16} />Use Ready Template</Button>
          <Button variant="outline" className="gap-1.5" onClick={openSettings} data-testid="admit-card-settings-btn"><Settings2 size={16} />Admit Card Settings</Button>
          <Button className="gap-1.5 bg-gradient-to-r from-violet-600 to-indigo-600" onClick={() => nav("/templates/designer")} data-testid="admit-card-canvas-btn"><Palette size={16} />Canvas</Button>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap rounded-xl border border-slate-200 bg-white px-4 py-2.5" data-testid="ac-layout-bar">
        <PanelsTopLeft size={16} className="text-slate-400" />
        <span className="text-sm text-slate-500">Printed layout:</span>
        {boundTpl ? (
          <>
            <span className="text-sm font-semibold text-indigo-600" data-testid="ac-bound-name">{boundTpl.name}</span>
            <Button size="sm" variant="ghost" className="h-7 gap-1 text-slate-500" onClick={() => setBindOpen(true)} data-testid="ac-change-layout">Change</Button>
            <Button size="sm" variant="ghost" className="h-7 gap-1 text-rose-500" onClick={unbindTemplate} data-testid="ac-unbind-layout"><XIcon size={13} />Use default</Button>
          </>
        ) : (
          <>
            <span className="text-sm font-semibold text-slate-700">Default admit card</span>
            <Button size="sm" variant="outline" className="h-7 gap-1.5" onClick={() => setBindOpen(true)} data-testid="ac-bind-layout"><LayoutTemplate size={14} />Use a Canvas layout</Button>
          </>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-3 flex flex-wrap items-center gap-3">
        <Select value={singleClass} onValueChange={(v) => { setSingleClass(v); setExamId(""); setExamData(null); setStudents([]); }}>
          <SelectTrigger className="w-56" data-testid="ac-class-select"><SelectValue placeholder="Select a class" /></SelectTrigger>
          <SelectContent>{classesFromExams.map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={examId} onValueChange={setExamId}>
          <SelectTrigger className="w-72" data-testid="ac-exam-select"><SelectValue placeholder="Select an exam" /></SelectTrigger>
          <SelectContent>{(singleClass ? exams.filter((e) => e.class_id === singleClass) : exams).map((e) => <SelectItem key={e.id} value={e.id}>{e.name} · {e.class_name} · {e.term}</SelectItem>)}</SelectContent>
        </Select>
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9" placeholder="Search name, admission no, roll…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="ac-search" />
        </div>
        <div className="ml-auto flex items-center gap-3">
          {selected.size > 0 && (
            <Button size="sm" className="gap-1.5" onClick={() => printList([...selected])} data-testid="ac-generate-selected"><Printer size={14} />Generate Selected ({selected.size})</Button>
          )}
          {students.length > 0 && <Button size="sm" variant="outline" onClick={() => printList(null)} className="gap-1.5" data-testid="ac-print-all"><Printer size={14} />Print All ({students.length})</Button>}
          {examData && <span className="text-xs text-slate-400">{filtered.length} of {students.length}</span>}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 table-scroll">
        {loading ? <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-indigo-500" /></div> :
          !examData ? <div className="py-16 text-center text-slate-400">Choose an exam to list its students.</div> :
          filtered.length === 0 ? <div className="py-16 text-center text-slate-400"><IdCard size={40} className="mx-auto mb-2 opacity-40" />No students found.</div> : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="px-4 py-3 w-10"><input type="checkbox" className="h-4 w-4 accent-indigo-600 cursor-pointer" checked={allChecked} onChange={toggleAll} data-testid="ac-select-all" /></th>
                  <th className="text-left px-4 py-3">Student</th>
                  <th className="text-left px-4 py-3">Admission No.</th>
                  <th className="text-left px-4 py-3">Roll</th>
                  <th className="text-left px-4 py-3">Class</th>
                  <th className="text-right px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((s) => (
                  <tr key={s.id} className="hover:bg-slate-50" data-testid={`ac-row-${s.admission_number}`}>
                    <td className="px-4 py-2.5"><input type="checkbox" className="h-4 w-4 accent-indigo-600 cursor-pointer" checked={selected.has(s.id)} onChange={() => toggleOne(s.id)} data-testid={`ac-select-${s.id}`} /></td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2.5">
                        {s.docs && s.docs.photo ? <img src={s.docs.photo} alt={s.name} className="w-9 h-9 rounded-full object-cover" /> : <div className="w-9 h-9 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-semibold shrink-0">{initials(s.name)}</div>}
                        <div>
                          <div className="font-medium text-slate-800">{s.name}</div>
                          {s.father_name && <div className="text-xs text-slate-400">S/o {s.father_name}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-slate-500">{s.admission_number}</td>
                    <td className="px-4 py-2.5 text-slate-500">{s.roll_number || "—"}</td>
                    <td className="px-4 py-2.5 text-slate-500">{s.class_name}</td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="inline-flex items-center gap-2">
                        <Button size="sm" variant="ghost" className="gap-1.5" onClick={() => preview(s.id)} data-testid={`ac-preview-${s.admission_number}`}><Eye size={14} />Preview</Button>
                        <Button size="sm" variant="outline" className="gap-1.5" disabled={busyId === s.id} onClick={() => generate(s.id)} data-testid={`ac-generate-${s.admission_number}`}>
                          {busyId === s.id ? <Loader2 size={14} className="animate-spin" /> : <Printer size={14} />}Generate
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      <Dialog open={!!card} onOpenChange={(o) => !o && setCard(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto p-0">
          <div className="print-area">{card && <AdmitCardView student={card.student} exam={card.exam} school={card.school} />}</div>
          <div className="flex justify-end gap-2 p-4 border-t border-slate-100 no-print">
            <Button variant="outline" onClick={() => setCard(null)}>Close</Button>
            <Button onClick={printSingle} className="gap-1.5" data-testid="ac-print-card-btn"><Printer size={16} />Print</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="w-[90vw] max-w-[90vw] max-h-[90vh] overflow-y-auto" data-testid="admit-card-settings-dialog">
          <DialogHeader><DialogTitle>Admit Card Settings</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-5">
            <div className="space-y-4 min-w-0">
              <div><Label>Card Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="ac-title" /></div>
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                <Label className="mb-2 block">Show on Admit Card</Label>
                <div className="grid grid-cols-2 gap-2">
                  {FIELD_TOGGLES.map(([k, lbl]) => (
                    <label key={k} className="flex items-center justify-between gap-2 py-1.5 px-3 rounded-lg border border-slate-100 bg-white">
                      <span className="text-sm text-slate-700">{lbl}</span>
                      <Switch checked={fields.includes(k)} onCheckedChange={() => toggleField(k)} data-testid={`ac-toggle-${k}`} />
                    </label>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Left Signatory</Label><Input value={sigLeft} onChange={(e) => setSigLeft(e.target.value)} data-testid="ac-sig-left" /></div>
                <div><Label>Right Signatory</Label><Input value={sigRight} onChange={(e) => setSigRight(e.target.value)} data-testid="ac-sig-right" /></div>
              </div>
            </div>
            <div>
              <Label className="mb-2 block">Preview</Label>
              <div className="bg-slate-100 rounded-lg p-4 overflow-auto" style={{ maxHeight: "70vh" }}>
                <div className="mx-auto" style={{ width: 620 }}>
                  <div className="shadow-lg"><AdmitCardView student={previewStudent} exam={previewExam} school={previewSchool} /></div>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSettingsOpen(false)}>Cancel</Button>
            <Button onClick={saveSettings} disabled={savingCs} className="gap-1.5" data-testid="ac-save-btn">{savingCs ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {examData && students.length > 0 && (
        <div className="print-area-bulk">
          {students.filter((s) => !filterIds || filterIds.includes(s.id)).map((s) => (
            <div key={s.id} className="report-card-page">
              <AdmitCardView student={s} exam={examData} school={school} />
            </div>
          ))}
        </div>
      )}

      <UseTemplateDialog
        open={tplOpen}
        onOpenChange={setTplOpen}
        categories={["Admit Card", "Academic", "General"]}
        heading="Use a Ready Template — Admit Card"
        getPayload={() => ({ exam_id: examId, student_ids: (selected.size ? filtered.filter((s) => selected.has(s.id)).map((s) => s.id) : filtered.map((s) => s.id)) })}
      />
      <UseTemplateDialog
        open={bindOpen}
        onOpenChange={setBindOpen}
        bindMode
        onPick={bindTemplate}
        categories={["Admit Card", "Academic", "General"]}
        heading="Choose a Canvas layout for Admit Cards"
      />
    </div>
  );
}
